import axiosApiInstance from '@/api/shared/axios-instance';
import CustomEquipoWithTooltip from '@/DesignSystem/components/partidos/CustomEquipoWithTooltip';
import { useFixtureStatus } from '@/DesignSystem/hooks/fixtures/useFixtureStatus';
import { useFixtureStatusShort } from '@/DesignSystem/hooks/fixtures/useFixtureStatusShort';
import { getDateAndHourFromDate } from '@/helpers/datetime-helper';
import URL_FACTORY from '@/helpers/url-helper';
import { Box, Typography, useTheme, IconButton } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Calendar2Icon from '../icons/calendar2-icon';
import RedDotBlinking from '@/components/shared/custom/in-progress';
import { Rutas } from '@/constants/rutas';
import { REDUCERS } from '@/constants/reducers';
import { Context } from '@/context';
import CustomLink from '../custom/custom-link';
import useIsUserPremium from '@/DesignSystem/hooks/useIsUserPremium';
import Skeletons from '@/components/home/Skeletons';
import { HttpStatusCode } from 'axios';
import { removeFromCookies } from '@/helpers/cookies';
import { SITE_USER_COOKIE } from '@/constants/site';

const STORIES_VIEWED_KEY = 'analitica_fantasy_stories_viewed';

const getViewedStories = () => {
    if (typeof window === 'undefined') return [];
    try {
        const viewed = localStorage.getItem(STORIES_VIEWED_KEY);
        return viewed ? JSON.parse(viewed) : [];
    } catch {
        return [];
    }
};

const enrichStoriesWithViewedStatus = (stories) => {
    if (!stories || stories.length === 0) return stories;
    const viewedStories = getViewedStories();
    const viewedSet = new Set(viewedStories);

    const result = stories.map((story) => ({
        ...story,
        isViewed: story.id ? viewedSet.has(story.id) : false,
    }));

    return result;
};

const mapFixtureData = (fixture) => {
    const { isUpcoming, isInProgress } = useFixtureStatus(fixture?.s);
    const { date, hour } = getDateAndHourFromDate(fixture?.d);
    const goles = `${fixture?.h?.g ?? ''}-${fixture?.a?.g ?? ''}`;
    const status = useFixtureStatusShort(fixture?.s);
    const bottom = isUpcoming ? hour : goles;

    return {
        id: fixture?.i,
        home: { id: fixture?.h?.i, name: fixture?.h?.n, g: fixture?.h?.g },
        away: { id: fixture?.a?.i, name: fixture?.a?.n, g: fixture?.a?.g },
        top: isUpcoming ? date : status,
        bottom,
        isInProgress,
        date: fixture?.d,
    };
};

const FixturesLayout = () => {
    const { state, dispatch } = useContext(Context);
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const hasFetched = useRef(false);
    const isUserPremium = useIsUserPremium();
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const scroll = (direction) => {
        const scrollAmount = 200;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
        }
    };

    const checkOverflow = () => {
        if (scrollContainerRef.current) {
            const hasOverflow =
                scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
            setShowScrollButtons(hasOverflow);
        }
    };

    const getPartidosForHome = async () => {
        if (hasFetched.current) return;

        axiosApiInstance
            .get(URL_FACTORY.GetPartidosForHome)
            .then((response) => {
                const data = response?.data?.f || [];
                const parsed = data.map(mapFixtureData);
                dispatch({
                    type: REDUCERS.UPDATE_PARTIDOS,
                    payload: parsed,
                });

                dispatch({
                    type: REDUCERS.UPDATE_CONFIGURATION,
                    payload: response?.data?.c,
                });

                const stories = response?.data?.s || [];
                const enrichedStories = enrichStoriesWithViewedStatus(stories);
                dispatch({
                    type: REDUCERS.UPDATE_STORIES,
                    payload: enrichedStories,
                });
                hasFetched.current = true;
            })
            .catch((error) => {
                if (error.response?.status === HttpStatusCode.PaymentRequired) {
                    removeFromCookies(SITE_USER_COOKIE);
                    window.location.href = Rutas.INICIO;
                }
            });
    };

    useEffect(() => {
        if ('requestIdleCallback' in window) {
            requestIdleCallback(getPartidosForHome);
        } else {
            setTimeout(getPartidosForHome, 200);
        }
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [state.partidos]);

    return (
        <Box
            sx={{
                height: { xs: 32, md: 32 },
                position: 'relative',
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}
                className="container"
            >
                {/* Icono calendario */}
                <Box
                    sx={{ display: 'flex', alignItems: 'center', width: 20, justifyContent: 'center', mx: 1 }}
                >
                    <CustomLink
                        href={Rutas.CALENDARIO_DE_PARTIDOS}
                        prefetch={false}
                        useWrapper={true}
                        useNativeLink={!isUserPremium}
                    >
                        <Calendar2Icon width={20} height={20} color={theme.palette.primary.main} />
                    </CustomLink>
                </Box>

                {/* Flecha izquierda */}
                {showScrollButtons && (
                    <IconButton
                        onClick={() => scroll(-1)}
                        sx={{
                            width: 32,
                            height: 32,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                transform: 'scale(1.05)',
                            },
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                        }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                )}

                {/* Scroll horizontal de partidos */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        overflowX: 'auto',
                        width: {
                            xs: 'calc(100% - 30px)',
                            md: showScrollButtons ? 'calc(100% - 100px)' : 'calc(100% - 30px)',
                        },
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {state.partidos
                        ?.filter(
                            (partido, index, self) => index === self.findIndex((p) => p.id === partido.id)
                        )
                        ?.sort((a, b) => new Date(a.date) - new Date(b.date))
                        ?.map((partido, index) => {
                            const ruta = `${Rutas.PARTIDO}/${partido.id}/alineaciones-probables`;
                            return (
                                <CustomLink
                                    key={partido.id}
                                    href={ruta}
                                    prefetch={false}
                                    useWrapper={true}
                                    useNativeLink={!isUserPremium}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        sx={{
                                            borderRight: `1px solid ${theme.palette.border.main}`,
                                            borderLeft:
                                                index === 0
                                                    ? `1px solid ${theme.palette.border.main}`
                                                    : 'none',
                                            px: 1,
                                            gap: 0.5,
                                        }}
                                    >
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <Typography
                                                component="span"
                                                fontSize={10}
                                                sx={{ display: 'flex', alignItems: 'center' }}
                                                noWrap
                                            >
                                                {partido.isInProgress && (
                                                    <RedDotBlinking
                                                        size={5}
                                                        style={{ marginRight: 2 }}
                                                        hideText={true}
                                                    />
                                                )}
                                                {partido.top}
                                            </Typography>
                                            <Typography fontSize={10} noWrap>
                                                {partido.bottom}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <CustomEquipoWithTooltip
                                                teamId={partido.home.id}
                                                alt={partido.home.name}
                                                width={20}
                                                height={20}
                                            />
                                            <CustomEquipoWithTooltip
                                                teamId={partido.away.id}
                                                alt={partido.away.name}
                                                width={20}
                                                height={20}
                                            />
                                        </Box>
                                    </Box>
                                </CustomLink>
                            );
                        })}

                    {state.partidos === null ||
                        (state.partidos.length === 0 && (
                            <Skeletons amount={20} gap={1} height={25} width={80} isHorizontal={true} />
                        ))}
                </Box>

                {/* Flecha derecha */}
                {showScrollButtons && (
                    <IconButton
                        onClick={() => scroll(1)}
                        sx={{
                            width: 32,
                            height: 32,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                transform: 'scale(1.05)',
                            },
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                        }}
                    >
                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

export default FixturesLayout;
